import type { Database } from '#shared/generated/supabase'
import type { UserMembershipRecord } from '@/types/member/memberTypes'
import type { UseSessionMembershipDeps } from '@/types/session/sessionTypes'
import { useQuery } from '@tanstack/vue-query'
import { membershipKeys } from '@/queries/membership/membershipKeys'

/** Resolves the Supabase client now, in setup, where the Nuxt context is available. */
function fetchWithSupabase() {
  const supabase = useSupabaseClient<Database>()
  // Imported on first use: its parsers bring zod, which a visitor with no session never needs.
  return async (id: string) => {
    const { fetchUserMembership } = await import('@/queries/membership/membershipQueries')
    return fetchUserMembership(supabase, id)
  }
}

/**
 * The signed-in user's membership record, or `null` when they have none. Every reader
 * shares one cache entry under `membershipKeys.user(userId)`.
 */
export function useSessionMembership(deps: UseSessionMembershipDeps = {}) {
  const getUser = deps.getUser ?? (() => useSupabaseUser().value)
  const fetchMembership = deps.fetchMembership ?? fetchWithSupabase()

  const userId = computed(() => {
    const sub = getUser()?.sub
    return sub != null && sub !== '' ? sub : undefined
  })

  const membershipQuery = useQuery<UserMembershipRecord | null, Error>({
    queryKey: computed(() => membershipKeys.user(userId.value)),
    queryFn: () => {
      const id = userId.value
      if (id == null)
        throw new Error('userId is required')
      return fetchMembership(id)
    },
    enabled: computed(() => import.meta.client && userId.value != null),
    retry: false,
  })

  return { membershipQuery }
}
